import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button, Typography} from '@mui/material';
import {Link, useHistory, useLocation} from 'react-router-dom';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import GCFLogo from '../../../assets/images/cpc/CPCIndexLogo-white.png';
import {
    cpcGovLogoutService,
    getCpciCountriesService,
    getCpcGovUser,
    isCpcGovLoggedIn,
} from '../../../services/cpcGov.service';

const NAVY_DEEP = '#003D69';
const LOGIN_PATH = '/dq_pro/cpc-government-dashboard/login';
const LANDING_PATH = '/dq_pro/cpc-government-dashboard';

/**
 * Shared CPC Government Dashboard header — logo left, page-specific
 * action (pill / back link) passed in via `right`.
 *
 * The signed-in account block lives HERE rather than on each page so that every
 * screen behind the login gets a way out. A reviewer who cannot sign out on the
 * screen they are standing on has no way to hand the machine to a colleague, and
 * the session is what scopes their country — leaving it open is the one thing
 * this dashboard must not do.
 */
const CpcHeader = ({right, hideSignOut}) => {
    const history = useHistory();
    const location = useLocation();
    const [signingOut, setSigningOut] = useState(false);
    // "Sign in" belongs on the public entry point only. Disclaimer and User
    // Information are steps AFTER login — once accounts are live nobody reaches
    // them signed out, so offering to sign in there is dev-only clutter.
    const onLandingPage = location.pathname.replace(/\/+$/, '') === LANDING_PATH;

    const authed = isCpcGovLoggedIn();
    const user = authed ? getCpcGovUser() : null;

    // Resolves the account's country_code to its full UN name, same as the
    // dashboard sidebar — a bare "KR" reads as an internal code, not a place.
    const [countries, setCountries] = useState([]);
    useEffect(() => {
        if (!authed) return undefined;
        let alive = true;
        getCpciCountriesService()
            .then((res) => {
                const list = res?.data?.countries;
                if (alive && Array.isArray(list)) setCountries(list);
            })
            .catch(() => { /* best effort — the code still displays, just unresolved */ });
        return () => { alive = false; };
    }, [authed]);

    // country_code is the account's fixed scope; fall back to the email so the
    // block never renders empty for an account still missing its country.
    const accountLabel = useMemo(() => {
        if (!user) return '';
        if (!user.country_code) return user.email || '';
        const hit = countries.find((c) => c.country_code === user.country_code);
        return hit ? hit.country_name : user.country_code;
    }, [user, countries]);

    const signOut = async () => {
        if (signingOut) return;
        setSigningOut(true);
        // The service clears the local session even if the server call fails, so
        // this can never strand someone in a signed-in-looking state.
        await cpcGovLogoutService();
        history.push(LOGIN_PATH);
    };

    return (
        <Box
            component="header"
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 5,
                height: 88,
                background: NAVY_DEEP,
                boxShadow: '0 4px 6px rgba(0,0,0,.1)',
                '@media (max-width: 720px)': {
                    height: 'auto',
                    minHeight: 72,
                    px: 2.5,
                    py: 1.75,
                    flexWrap: 'wrap',
                },
            }}
        >
            <Box
                component={Link}
                to={LANDING_PATH}
                aria-label="Go to the National CPC Index home page"
                sx={{display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, textDecoration: 'none'}}
            >
                <Box
                    component="img"
                    src={GCFLogo}
                    alt=""
                    sx={{
                        height: 100,
                        width: 'auto',
                        display: 'block',
                        filter: 'brightness(0) invert(1)',
                        '@media (max-width: 720px)': {height: 30},
                    }}
                />
            </Box>

            <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0, flexWrap: 'wrap', justifyContent: 'flex-end'}}>
                {right && (
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                        {right}
                    </Box>
                )}

                {/* Public entry point only — see onLandingPage above. */}
                {!authed && onLandingPage && (
                    <Button
                        onClick={() => history.push(LOGIN_PATH)}
                        sx={{
                            textTransform: 'none',
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,.45)',
                            px: 2,
                            py: 0.6,
                            borderRadius: '999px',
                            whiteSpace: 'nowrap',
                            '&:hover': {background: 'rgba(255,255,255,.1)'},
                        }}
                    >
                        Sign in
                    </Button>
                )}

                {authed && (accountLabel || !hideSignOut) && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            minWidth: 0,
                            ...(right && {
                                pl: 2.5,
                                borderLeft: '1px solid rgba(255,255,255,.2)',
                                '@media (max-width: 720px)': {pl: 0, borderLeft: 'none'},
                            }),
                        }}
                    >
                        {accountLabel && (
                            <Typography
                                sx={{
                                    color: 'rgba(255,255,255,.92)',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    letterSpacing: '.02em',
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: '999px',
                                    border: '1px solid rgba(255,255,255,.35)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 220,
                                    '@media (max-width: 720px)': {display: 'none'},
                                }}
                                title={accountLabel}
                            >
                                {accountLabel}
                            </Typography>
                        )}
                        {!hideSignOut && (
                            <Button
                                onClick={signOut}
                                disabled={signingOut}
                                startIcon={<PowerSettingsNewIcon fontSize="small"/>}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: 13.5,
                                    fontWeight: 700,
                                    color: NAVY_DEEP,
                                    background: '#fff',
                                    px: 2,
                                    py: 0.6,
                                    borderRadius: '999px',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {background: 'rgba(255,255,255,.88)'},
                                    '&.Mui-disabled': {
                                        background: 'rgba(255,255,255,.45)',
                                        color: NAVY_DEEP,
                                    },
                                }}
                            >
                                {signingOut ? 'Signing out…' : 'Sign out'}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CpcHeader;
